<template>
    <div id="main">
        <div class="alert-wrapper">
            <alert-container :messages="msgs" />
        </div>

        <!-- Título -->
        <div class="atendimento">
            <h1 class="title">{{ $t("faturamento") }}</h1>
            <nav class="breadcrumb" aria-label="breadcrumbs">
                <ul>
                    <li>
                        <a>{{ $t("vendas") }}</a>
                    </li>
                    <li>
                        <a>{{ $t("relatorios") }}</a>
                    </li>
                    <li>
                        <a href="#" aria-current="page">{{
                            $t("faturamento")
                        }}</a>
                    </li>
                </ul>
            </nav>
        </div>

        <FiltroRelatorio
            ref="filtroRelatorio"
            :allColunas="fieldsTable"
            :colsSelecionadas="fieldsSel"
            :filters="filtrosRel"
            :filtrosAtuais="filtrosSel"
            :getFiltro="getDadosFiltro"
            :estados="states"
            @aplicar-filtro="aplicarFiltro"
            @exportar="exportar"
            @clean="limparFiltros"
            :permissao="{ export: permissao.export }"
        />

        <loader v-if="isLoading" />

        <div class="page" v-if="!error">
            <!-- Tabela Sem Agrupamento -->
            <b-table
                ref="resultsTable"
                class="relBueTable"
                v-if="
                    !agrupando && !states.isSearching && !firstTime && !noData
                "
                v-show="states.allowButtons"
                :data="dadosTabela.length > 0 ? dadosTabela : [{}]"
                :per-page="perPage"
                :current-page.sync="currentPage"
                paginated
                narrowed
            >
                <b-table-column
                    v-for="(f, i) in fieldsTable"
                    :key="i"
                    :field="f"
                    :visible="checkIfSelected(f)"
                    :label="$t(f)"
                    :custom-sort="f == 'dataemissao' ? sortByDate : null"
                    sortable
                    v-show="states.allowButtons"
                    v-slot="props"
                >
                    <div class="is-pulled-right" v-if="f == 'valor'">
                        {{
                            props.row[f] != undefined
                                ? props.row[f].toLocaleString(undefined, {
                                      minimumFractionDigits: 2,
                                  })
                                : ""
                        }}
                    </div>
                    <div v-else-if="f == 'dataemissao'">
                        {{ props.row[f] ? getDateString(props.row[f]) : "" }}
                    </div>
                    <div v-else>
                        {{ props.row[f] }}
                    </div>
                </b-table-column>
            </b-table>

            <!-- Tabela Agrupando -->
            <b-table
                ref="resultsTableAgrupado"
                class="relBueTable"
                v-if="agrupando && !states.isSearching && !firstTime && !noData"
                :detailed="true"
                :data="dadosAgrupamento.length > 0 ? dadosAgrupamento : [{}]"
                :paginated="true"
                :narrowed="true"
                :per-page="perPage"
                :current-page.sync="currentPage"
                v-show="states.allowButtons"
            >
                <b-table-column
                    :field="agruparField"
                    :label="$t(agruparField)"
                    :custom-sort="
                        agruparField == 'dataemissao' ? sortByDate : null
                    "
                    sortable
                    v-slot="props"
                >
                    <div v-if="agruparField == 'valor'">
                        {{
                            props.row[agruparField] != undefined
                                ? props.row[agruparField].toLocaleString(
                                      undefined,
                                      { minimumFractionDigits: 2 }
                                  )
                                : ""
                        }}
                    </div>
                    <div v-else-if="agruparField == 'dataemissao'">
                        {{
                            props.row[agruparField]
                                ? getDateString(props.row[agruparField])
                                : ""
                        }}
                    </div>
                    <div v-else>
                        {{ props.row[agruparField] }}
                    </div>
                </b-table-column>

                <template slot="detail" slot-scope="props">
                    <b-table
                        :data="dadosTabelaAgrupado[props.row[agruparField]]"
                        :paginated="true"
                        :per-page="10"
                        :current-page.sync="props.row['currentPage']"
                    >
                        <b-table-column
                            v-for="(f, i) in fieldsTable"
                            :class="f"
                            :key="i"
                            :field="f"
                            :visible="checkIfSelected(f)"
                            :label="$t(f)"
                            :custom-sort="
                                f == 'dataemissao' ? sortByDate : null
                            "
                            sortable
                            v-slot="props"
                        >
                            <div class="is-pulled-right" v-if="f == 'valor'">
                                {{
                                    props.row[f] != undefined
                                        ? props.row[f].toLocaleString(
                                              undefined,
                                              { minimumFractionDigits: 2 }
                                          )
                                        : ""
                                }}
                            </div>
                            <div v-else-if="f == 'dataemissao'">
                                {{
                                    props.row[f]
                                        ? getDateString(props.row[f])
                                        : ""
                                }}
                            </div>
                            <div v-else>
                                {{ props.row[f] }}
                            </div>
                        </b-table-column>
                    </b-table>
                </template>
            </b-table>

            <!-- Loader -->
            <div v-show="states.isSearching" class="searching-loader">
                <svg class="loading">
                    <use
                        :xlink:href="
                            require('@/assets/img/icons/loader.svg') + '#layer'
                        "
                    ></use>
                </svg>
                <div>{{ $t("carregando-dados") }}...</div>
            </div>

            <!-- Mensagem 'não há dados' e ajuda de utilização -->
            <div
                v-show="(firstTime || noData) && !states.isSearching"
                class="searching-loader"
            >
                <svg class="bigicon">
                    <use
                        v-if="firstTime"
                        :xlink:href="
                            require('@/assets/img/icons/magnify.svg') + '#layer'
                        "
                    ></use>
                    <use
                        v-else
                        :xlink:href="
                            require('@/assets/img/icons/chart-error.svg') +
                            '#layer'
                        "
                    ></use>
                </svg>
                <div v-if="firstTime">
                    {{ $t("descricao-ajuda-filtro-relatorio") }}
                </div>
                <div v-else>
                    {{ $t("no-data") }}
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import VendasFaturamentoService from "@/services/VendasFaturamentoService";
import FiltroRelatorioService from "@/services/FiltroRelatorioService";
import FiltroRelatorio from "@/components/promotor/relatorio/FiltroRelatorio";
import Message from "@/models/Message";
import ColaboradorService from "@/services/ColaboradorService";
import TipoPedidoService from "@/services/vendas/TipoPedidoService";
import { mapState } from 'vuex';

export default {
    components: {
        FiltroRelatorio,
    },
    data() {
        return {
            id: 0,
            idrelatorio: 0,
            idusuario: 0,
            descricao: "relatorio-vendas-faturamento",
            service: null,
            filtroService: null,
            tipoPedidoService: new TipoPedidoService(this.$http, this.$router),
            // Todos as colunas da tabela
            fieldsTable: [
                "cliente",
                "vendedor",
                "cidade",
                "estado",
                "regiao",
                "empresa",
                "valor",
                "dataemissao",
            ],

            // Colunas que já devem vir pré-selecionadas nas opções
            fieldsSel: [],

            // Todos os filtros para disponibilizar no relatório
            filtrosRel: [
                { label: "cidade" },
                { label: "estado" },
                { label: "regiao" },
                { label: "cliente" },
                { label: "vendedor" },
                { label: "empresa" },
                { label: "atividade" },
                { label: "grupoproduto" },
                { label: "subgrupoproduto" },
                { label: "produto" },
                { label: "tipo-pedido" },
            ],
            ordem: "dataemissao",
            dadosTabela: [],
            dadosAgrupamento: [],
            dadosTabelaAgrupado: [],
            filtrosSel: [],
            agrupando: false,
            agruparField: null,
            states: {
                isSearching: false,
                isExporting: false,
                allowButtons: false,
            },
            isLoading: false,
            currentPage: 1,
            perPage: 20,
            totalRows: 0,
            modifiers: [
                { type: "IN", label: "e" },
                { type: "NOT_IN", label: "nao-e" },
            ],
            msgs: [],
            firstTime: true,
            error: false,
            noData: false,
            permissao: {},
            colaboradorService: null,
            empresas: [0]
        };
    },
    created() {
        this.permissao = this.hasPermissionAccess(
            "vendas",
            "relatorio-analitico",
            "relatorio-vendas-faturamento"
        );

        !this.permissao.read &&
            this.$router.push({
                name: "acesso-nao-permitido",
                params: { tela: "faturamento" },
            });

        this.service = new VendasFaturamentoService(this.$http, this.$router);
        this.filtroService = new FiltroRelatorioService(
            this.$http,
            this.$router
        );
        this.colaboradorService = new ColaboradorService(
            this.$http,
            this.$router
        );

        this.loadEmpresasId();

        this.service.getFiltros().then((r) => {
            this.fieldsSel = r.colunas;

            if (r.id != undefined) {
                this.id = r.id;
                this.idrelatorio = r.idrelatorio;
                this.idusuario = r.idusuario;
            }

            if (r.agrupadopor != undefined && r.agrupadopor != "") {
                this.$refs.filtroRelatorio.setAgrupamento(r.agrupadopor);
            }

            r.filtros.forEach((f) => {
                this.$refs.filtroRelatorio.addFilter(f);
            });

            setTimeout(() => {
                this.$refs.filtroRelatorio.mountColumns();
                this.states.allowButtons = true;
            }, 10);
        });

        /* Inserindo filtros obrigatórios antes */
        this.filtrosSel.unshift({
            label: "fim",
            required: true,
            selected: true,
            type: "date",
            searchModifiers: [{ type: "IN", label: "e" }],
            modifierValue: { type: "IN", label: "e" },
            selectedValue: this.$moment().format("L"),
        });

        this.filtrosSel.unshift({
            label: "inicio",
            required: true,
            selected: true,
            type: "date",
            searchModifiers: [{ type: "IN", label: "e" }],
            modifierValue: { type: "IN", label: "e" },
            selectedValue: this.$moment().subtract(1, "M").format("L"),
        });
    },
    mounted() {
        let iniInput =
            this.$refs.filtroRelatorio.$refs["dateFilterOption-inicio"][0].$el;
        iniInput.onpaste = this.prevent;
        iniInput.ondragenter = this.prevent;
        iniInput.ondragover = this.prevent;
        iniInput.ondrop = this.prevent;

        let fimInput =
            this.$refs.filtroRelatorio.$refs["dateFilterOption-fim"][0].$el;
        fimInput.onpaste = this.prevent;
        fimInput.ondragenter = this.prevent;
        fimInput.ondragover = this.prevent;
        fimInput.ondrop = this.prevent;
    },
    methods: {
        checkIfSelected(f) {
            return (
                this.fieldsSel.findIndex((e) => {
                    return e == f;
                }) > -1
            );
        },
        getFiltros(f) {
            let atributos = {
                id: this.id ? this.id : null,
                idrelatorio: this.idrelatorio ? this.idrelatorio : null,
                idusuario: this.idusuario ? this.idusuario : null,
                datainicial: "",
                datafinal: "",
                descricao: this.descricao,
                colunas: [],
                filtros: [],
                agrupadopor: f.agrupar ? f.agrupar : "",
            };

            atributos.colunas = this.fieldsSel;

            f.filtros.forEach((ftr) => {
                if (ftr.type != "date") {
                    let r = {
                        nome: ftr.label,
                        regra: ftr.modifierValue.type,
                        idrelatorio: atributos.idrelatorio,
                        ids: [],
                    };

                    if (ftr.type == "select") {
                        if (ftr.selectedValue != null) {
                            ftr.selectedValue.forEach((v) => {
                                r.ids.push(v.id);
                            });
                        }
                    }

                    atributos.filtros.push(r);
                } else {
                    if (ftr.label == "fim") {
                        atributos.datafinal = this.getDate(ftr.selectedValue);
                    } else if (ftr.label == "inicio") {
                        atributos.datainicial = this.getDate(ftr.selectedValue);
                    }
                }
            });

            if (
                atributos.filtros.some(function (elem) {
                    return elem.nome == "cidade" || elem.nome == "estado";
                })
            ) {
                let index = atributos.filtros.findIndex(function (elem) {
                    return elem.nome == "estado";
                });
                if (index > -1) {
                    let removeItem = atributos.filtros.splice(index, 1);
                    atributos.filtros.push(removeItem[0]);
                }
            }

            return atributos;
        },
        validate() {
            let valid = true;
            let dataini = this.filtrosSel[0].selectedValue;
            let datafim = this.filtrosSel[1].selectedValue;
            let m_ini = this.$moment(dataini, "L");
            let m_fim = this.$moment(datafim, "L");

            if (dataini.length < 10 || !m_ini._isValid) {
                this.msgs.push(
                    new Message("error", "msg-data-inicial-invalida", true)
                );
                valid = false;
            }

            if (datafim.length < 10 || !m_fim._isValid) {
                this.msgs.push(
                    new Message("error", "msg-data-final-invalida", true)
                );
                valid = false;
            }

            if (valid && m_fim.isBefore(m_ini)) {
                this.msgs.push(
                    new Message("error", "msg-erro-data-inicial-final", true)
                );
                valid = false;
            }

            return valid;
        },
        aplicarFiltro(f) {
            if (this.validate()) {
                if (this.firstTime) {
                    this.firstTime = false;
                }

                this.states.isSearching = true;

                let atributos = this.getFiltros(f);

                this.filtrosSel = this.filtrosSel.filter((e) => {
                    return e.selected;
                });

                this.service
                    .saveFiltros(atributos)
                    .then((r) => {
                        this.noData = r.data.length == 0;

                        if (
                            r.agrupadopor != "" &&
                            this.fieldsTable.includes(r.agrupadopor)
                        ) {
                            this.agruparField = r.agrupadopor;
                            this.dadosTabelaAgrupado = [];
                            this.dadosAgrupamento = [];

                            r.data.forEach((d) => {
                                let field = this.agruparField;

                                if (
                                    this.dadosTabelaAgrupado[d[field]] ==
                                    undefined
                                ) {
                                    this.dadosTabelaAgrupado[d[field]] = [];
                                    d.currentPage = 1;
                                    this.dadosAgrupamento.push(d);
                                }

                                this.dadosTabelaAgrupado[d[field]].push(d);
                            });

                            this.agrupando = true;
                        } else {
                            if (this.agrupando) {
                                setTimeout(() => {
                                    this.$refs.resultsTable.currentSortColumn =
                                        {};
                                    this.reorderColumns();
                                }, 50);
                            }

                            this.agrupando = false;
                        }

                        this.dadosTabela = r.data;
                        this.states.isSearching = false;

                        /**
                         * @84286 - caso seja a primeira busca do usuário o idrelatorio fica como 0,
                         * se o mesmo não atualiza a página e realiza novas buscas com novos filtros,
                         * os mesmos não serão atualizados por quê no back-end ele vai identificar que o id do relatório
                         * está como nulo e irá realizar uma novo cadastro.
                         */
                        this.idrelatorio = r.idrelatorio;
                    })
                    .catch((r) => {
                        this.error = true;
                        this.msgs.push(
                            new Message("error", "msg-erro-relatorio", true)
                        );

                        this.states.isSearching = false;
                        this.dadosTabela = [];
                        this.dadosTabelaAgrupado = [];
                    });
            }
        },
        getDadosFiltro(filtro) {
            /*  
          Estrutura do objeto a retornar
            type: 
              String: 
                'select', 'input' ou 'date'

            searchModifiers:
              Array de objetos: 
                contém modificadores dos filtros (é, não é, contém, etc)
                cada objeto tem pelo menos uma propriedade 'label' com a key do dicionário 
                
            modifierValue:
              Objeto:
                null ou um objeto do searchModifiers, será o valor selecionado por default

            searchFunction:
              Function:
                função de pesquisa que será chamada no select

            options (somente com type 'select'):
              Array de objetos: 
                contém as opções a serem selecionadas
                cada objeto tem pelo menos uma propriedade 'label' com a key do dicionário

            multiple (somente com type 'select'):
              Boolean:
                permite escolher mais de um valor

            selectedValue:
              Objeto (para type 'select'):
                null ou um array de objetos do options, será o(s) valor(es) selecionado(s) por default
              String (para os outros types):
                null ou a string preenchida por default
        */
            let nome = filtro.label != undefined ? filtro.label : filtro.nome;

            switch (nome) {
                case "cidade":
                    return {
                        type: "select",
                        multiple: true,
                        searchModifiers: this.modifiers,
                        modifierValue:
                            filtro.regra != undefined
                                ? this.getRegra(filtro.regra)
                                : this.modifiers[0],
                        searchFunction: this.loadCidade,
                        ids: filtro.ids != undefined ? filtro.ids : null,
                        selectedValue: null,
                        options: [],
                    };
                case "estado":
                    return {
                        type: "select",
                        multiple: true,
                        searchModifiers: this.modifiers,
                        modifierValue:
                            filtro.regra != undefined
                                ? this.getRegra(filtro.regra)
                                : this.modifiers[0],
                        searchFunction: this.loadEstado,
                        ids: filtro.ids != undefined ? filtro.ids : null,
                        selectedValue: null,
                        options: [],
                    };
                case "regiao":
                    return {
                        type: "select",
                        multiple: true,
                        searchModifiers: this.modifiers,
                        modifierValue:
                            filtro.regra != undefined
                                ? this.getRegra(filtro.regra)
                                : this.modifiers[0],
                        searchFunction: this.loadRegiao,
                        ids: filtro.ids != undefined ? filtro.ids : null,
                        selectedValue: null,
                        options: [],
                    };
                case "cliente":
                    return {
                        type: "select",
                        multiple: true,
                        searchModifiers: this.modifiers,
                        modifierValue:
                            filtro.regra != undefined
                                ? this.getRegra(filtro.regra)
                                : this.modifiers[0],
                        searchFunction: this.loadCliente,
                        ids: filtro.ids != undefined ? filtro.ids : null,
                        selectedValue: null,
                        options: [],
                    };
                case "vendedor":
                    return {
                        type: "select",
                        multiple: true,
                        searchModifiers: this.modifiers,
                        modifierValue:
                            filtro.regra != undefined
                                ? this.getRegra(filtro.regra)
                                : this.modifiers[0],
                        searchFunction: this.loadVendedor,
                        ids: filtro.ids != undefined ? filtro.ids : null,
                        selectedValue: null,
                        options: [],
                    };
                case "empresa":
                    return {
                        type: "select",
                        multiple: true,
                        searchModifiers: this.modifiers,
                        modifierValue:
                            filtro.regra != undefined
                                ? this.getRegra(filtro.regra)
                                : this.modifiers[0],
                        searchFunction: this.loadEmpresa,
                        ids: filtro.ids != undefined ? filtro.ids : null,
                        selectedValue: null,
                        options: [],
                    };
                case "atividade":
                    return {
                        type: "select",
                        multiple: true,
                        searchModifiers: this.modifiers,
                        modifierValue:
                            filtro.regra != undefined
                                ? this.getRegra(filtro.regra)
                                : this.modifiers[0],
                        searchFunction: this.loadAtividade,
                        ids: filtro.ids != undefined ? filtro.ids : null,
                        selectedValue: null,
                        options: [],
                    };
                case "grupoproduto":
                    return {
                        type: "select",
                        multiple: true,
                        searchModifiers: this.modifiers,
                        modifierValue:
                            filtro.regra != undefined
                                ? this.getRegra(filtro.regra)
                                : this.modifiers[0],
                        searchFunction: this.loadGrupoProduto,
                        ids: filtro.ids != undefined ? filtro.ids : null,
                        selectedValue: null,
                        options: [],
                    };
                case "subgrupoproduto":
                    return {
                        type: "select",
                        multiple: true,
                        searchModifiers: this.modifiers,
                        modifierValue:
                            filtro.regra != undefined
                                ? this.getRegra(filtro.regra)
                                : this.modifiers[0],
                        searchFunction: this.loadSubgrupoProduto,
                        ids: filtro.ids != undefined ? filtro.ids : null,
                        selectedValue: null,
                        options: [],
                    };
                case "produto":
                    return {
                        type: "select",
                        multiple: true,
                        searchModifiers: this.modifiers,
                        modifierValue:
                            filtro.regra != undefined
                                ? this.getRegra(filtro.regra)
                                : this.modifiers[0],
                        searchFunction: this.loadProduto,
                        ids: filtro.ids != undefined ? filtro.ids : null,
                        selectedValue: null,
                        options: [],
                    };
                case "tipo-pedido":
                    return {
                        type: "select",
                        multiple: true,
                        searchModifiers: this.modifiers,
                        modifierValue:
                            filtro.regra != undefined
                                ? this.getRegra(filtro.regra)
                                : this.modifiers[0],
                        searchFunction: this.loadTipoPedido,
                        ids: filtro.ids != undefined ? filtro.ids : null,
                        selectedValue: null,
                        options: [],
                    };
            }
        },
        compareDatesString(ini, fim) {
            if (ini && fim) {
                let vis = ini.split("/");
                let piDate = new Date(vis[2], vis[1] - 1, vis[0]);

                let vfs = fim.split("/");
                let pfDate = new Date(vfs[2], vfs[1] - 1, vfs[0]);

                if (piDate <= pfDate) {
                    return true;
                }
            }
            return false;
        },
        getDate(date) {
            return date.split("/").reverse().join("-");
        },
        loadCliente(v, id = false) {
            if (id) {
                return this.filtroService
                    .findClienteVendasById(v)
                    .then(this.mapItemsIdRSocial);
            } else {
                return this.filtroService
                    .findAllClienteVendas(50, v)
                    .then(this.mapItemsRSocial);
            }
        },
        loadCidade(v, id = false) {
            if (id) {
                return this.filtroService
                    .findCidadeById(v)
                    .then(this.mapItemsIdDesc);
            } else {
                return this.filtroService
                    .findAllCidade(0, 50, v)
                    .then(this.mapItemsDesc);
            }
        },
        loadEstado(v, id = false) {
            if (id) {
                return this.filtroService
                    .findEstadoById(v)
                    .then(this.mapItemsIdDesc);
            } else {
                return this.filtroService
                    .findAllEstado(0, 50, v)
                    .then(this.mapItemsDesc);
            }
        },
        /*
      @70881 - alterado para trazer a lista de vendedores vinculados ao usuario.
    */
        loadVendedor(v, id = false) {
            if (id) {
                return this.filtroService
                    .findVendedorById(v)
                    .then(this.mapItemsIdNome);
            } else {
                return this.colaboradorService
                    .findListAssociated(".*", 5000000)
                    .then(this.mapItemsNome);
            }
        },
        loadRegiao(v, id = false) {
            if (id) {
                return this.filtroService
                    .findRegiaoById(v)
                    .then(this.mapItemsIdDesc);
            } else {
                return this.filtroService
                    .findAllRegiao(v)
                    .then(this.mapItemsDesc);
            }
        },
        loadEmpresa(v, id = false) {
            if (id) {
                return this.filtroService
                    .findEmpresaById(v)
                    .then(this.mapItemsIdRSocial);
            } else {
                return this.filtroService
                    .findAllEmpresa(0, 50, v)
                    .then(this.mapItemsRSocial);
            }
        },
        loadAtividade(v, id = false) {
            if (id) {
                return this.filtroService
                    .findAtividadeById(v)
                    .then(this.mapItemsIdDesc);
            } else {
                return this.filtroService
                    .findAllAtividade(0, 50, v)
                    .then(this.mapItemsDesc);
            }
        },
        loadGrupoProduto(v, id = false) {
            if (id) {
                return this.filtroService
                    .findGrupoById(v)
                    .then(this.mapItemsIdDesc);
            } else {
                const empresasIds = this.empresas.join(",");
                return this.filtroService
                    .findAllGrupo(0, 50, 1, empresasIds, v)
                    .then(this.mapItemsDesc);
            }
        },
        loadSubgrupoProduto(v, id = false) {
            if (id) {
                return this.filtroService
                    .findSubgrupoById(v)
                    .then(this.mapItemsIdDesc);
            } else {
                return this.filtroService
                    .findAllSubgrupo(0, 50, 1, 0, v)
                    .then(this.mapItemsDesc);
            }
        },
        loadProduto(v, id = false) {
            if (id) {
                return this.filtroService
                    .findProdutoById(v)
                    .then(this.mapItemsIdDesc);
            } else {
                return this.filtroService
                    .findAllProduto(0, 50, v)
                    .then(this.mapItemsDesc);
            }
        },
        loadTipoPedido(v, id = false) {
            return this.tipoPedidoService
                .findAllCombo(v)
                .then(this.mapItemsDesc);
        },
        mapItemsNome(body) {
            let list = body.content.map((i) => {
                return {
                    id: i.id,
                    label: i.nome,
                };
            });
            return list;
        },
        mapItemsIdNome(body) {
            return {
                id: body.content.id,
                label: body.content.nome,
            };
        },
        mapItemsRSocial(body) {
            let list = body.content.map((i) => {
                return {
                    id: i.id,
                    label: i.rsocial,
                };
            });
            return list;
        },
        mapItemsIdRSocial(body) {
            return {
                id: body.content.id,
                label: body.content.rsocial,
            };
        },
        mapItemsDesc(body) {
            let list = body.content.map((i) => {
                return {
                    id: i.id,
                    label: i.descricao,
                };
            });
            return list;
        },
        mapItemsIdDesc(body) {
            return {
                id: body.content.id,
                label: body.content.descricao,
            };
        },
        getRegra(type) {
            return this.modifiers.find((m) => {
                return m.type == type;
            });
        },
        sortByDate(a, b, isAsc) {
            if (isAsc) {
                return (
                    new Date(a.dataemissao).getTime() -
                    new Date(b.dataemissao).getTime()
                );
            } else {
                return (
                    new Date(b.dataemissao).getTime() -
                    new Date(a.dataemissao).getTime()
                );
            }
        },
        getDateString(dateString) {
            // Considerando formato 'YYYY-MM-DD'
            let d = dateString.split("-");

            // Criando o Date usando argumentos separados por conta do Safari
            // Usando Date('YYYY-MM-DD') ou Date('YYYY-MM-DDT00:00:00Z') volta a data um dia por conta do timezone
            return new Date(d[0], d[1] - 1, d[2]).toLocaleDateString();
        },
        reorderColumns() {
            /* Reordena as colunas do b-table de acordo com as colunas selecionadas */
            var fields = this.fieldsSel;

            this.$refs.resultsTable.newColumns.sort(function (a, b) {
                let ia = fields.findIndex((e) => {
                    return e == a.field;
                });
                let ib = fields.findIndex((e) => {
                    return e == b.field;
                });

                if (ia >= 0 && ib >= 0) {
                    return ia - ib;
                } else {
                    if (ia == ib) return 0;
                    if (ia < 0) return 1;
                    if (ib < 0) return -1;
                }
            });
        },
        limparFiltros() {
            this.filtrosSel = this.filtrosSel.filter((f) => {
                return f.required;
            });
        },
        exportar(f) {
            let dataInicio = this.$moment(this.filtrosSel[0].selectedValue, "L");
            let dataFim = this.$moment(this.filtrosSel[1].selectedValue, "L");            
            let dataRange = dataFim.diff(dataInicio, 'months') < 6;
            if (this.validate() && dataRange) {
                this.states.isExporting = true;
                this.service.getCSV(this.getFiltros(f)).then(this.downloadCSV);
            } else {
                this.msgs.push(
                    new Message("error", "msg-data-range-limit-6-months", true)
                );
            }
        },
        downloadCSV(csv) {
            var filename, link, blob, url;

            if (csv == null) return;

            blob = new Blob([csv], { type: "data:text/csv;charset=utf-8;" });
            url = URL.createObjectURL(blob);

            filename = "faturamento.csv";

            link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.remove();

            this.states.isExporting = false;
        },
        prevent(e) {
            e.preventDefault();
        },
        loadEmpresasId() {
            if (this.$store.state.user.admin) {
                // se for admin, mostra todas as empresas
                this.$store.state.empresas.data.map((e) => {
                    this.empresas.push(e.id);
                });
            } else {
                // se for cliente, mostra todas as empresas relacionadas a ele
                this.$store.state.user.empresas.map((e) => {
                    this.empresas.push(e.id);
                });
            }
        },
    },
};
</script>
<style lang="scss" scoped>
#main {
    padding: 0 15px 15px 20px;
    height: auto;
}

h1.title {
    padding-top: 0;
    padding-bottom: 10px;
    margin-right: 0;
    margin-bottom: 10px;

    @media only screen and (max-width: 760px) {
        margin-top: 10px;
    }
}

.page {
    position: relative;
    background-color: $sovis-white;
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 2px;
    padding: 0.5rem 1rem 1rem 1rem;

    .title {
        margin-top: 0px;
        padding-top: 0px;
    }

    .atendimento .title {
        margin-bottom: 20px;
        margin-right: 0px;
    }

    .b-table {
        font-size: 12px !important;
    }

    .searching-loader {
        display: flex;
        flex-wrap: wrap;
        align-content: center;
        justify-content: center;
        width: 100%;
        height: 200px;

        div {
            width: 100%;
            text-align: center;
            font-size: 12px;
            font-style: normal;
            font-weight: 600;
            color: #898989;
            margin-top: 10px;
        }

        svg.loading {
            fill: #898989;
            width: 50px !important;
            height: 50px !important;
            animation-name: spin;
            animation-duration: 2000ms;
            animation-iteration-count: infinite;
            animation-timing-function: linear;
        }

        svg.bigicon {
            fill: #898989;
            width: 50px !important;
            height: 50px !important;
        }
    }
}
</style>